import { Injectable } from '@angular/core';

import { BetSlipType } from '@bpos';
import { BetSlip } from '@bpos/v2/bet-placement';
import { emptyOdds } from '@frontend/sports/odds/feature';
import { BetslipDisplayMode } from '@frontend/sports/types/components/bet-placement';
import { Action } from '@ngrx/store';
import { Decimal } from 'decimal.js';
import { values } from 'lodash-es';

import { IBetslipState } from '../../../base/store/state';
import { BaseBetslipGroupPick } from '../../../core/picks/betslip-group-pick';
import { BetslipPick } from '../../../core/picks/betslip-pick';
import { PriceType } from '../../../core/picks/pick-models';
import { OverAskBet } from '../../../model/over-ask/over-ask';
import { getComboSlipId, getSingleSlipId, getSystemSlipIdFromString } from '../../bet-generation/services/slip.utils';
import { ComboSlipType, SingleSlipType, SlipType } from '../../bet-generation/slip-type';
import { IBetPicks } from '../../betplacement/models';
import { isEachWay, isPickEachWayCapable } from '../../picks/services/betslip-pick-helpers';
import { reconvertSystem } from '../../system-bet/utils/system-type.helper';
import { BetslipOverAskActions } from '../actions';
import { OverAskErrorCreator } from '../state';

@Injectable({ providedIn: 'root' })
export class OverAskService {
    private bets: OverAskBet[] = [];

    constructor() {}

    initModern(betslipState: IBetslipState, betPicks: IBetPicks, betslip: BetSlip): void {
        this.bets = this.buildOverAskBetsFromBetslipState(betslipState, betPicks, betslip);
    }

    /**
     * Used in ngrx effect and returns action
     *
     * @param overAskOfferId
     * @param overAskOfferError
     * @param platform
     */
    createStartFlowAction(overAskOfferId: string, parentRequestId: string, overAskOfferError: OverAskErrorCreator): Action {
        const bets = [...this.bets];
        this.cleanUp();

        return BetslipOverAskActions.startFlow({
            offerId: overAskOfferId,
            parentRequestId,
            bets,
            overAskOfferError,
        });
    }

    cleanUp(): void {
        this.bets = [];
    }

    private buildOverAskBetsFromBetslipState(state: IBetslipState, betPicks: IBetPicks, betslip: BetSlip): OverAskBet[] {
        const pickList: { index: number; pick: BetslipPick }[] = [];
        const picks = Object.entries(betPicks.picks)
            .map(([key, value]) => ({ index: +key, pick: value }))
            .sort((a, b) => a.index - b.index);

        const groupedPicks: BaseBetslipGroupPick[] = [];
        const groupPicks: BetslipPick[] = [];
        const groups = Object.entries(betPicks.groups)
            .map(([key, value]) => ({ index: +key, pick: value }))
            .sort((a, b) => a.index - b.index);

        if (groups.length > 0) {
            const keys = Object.keys(groups);

            keys.forEach((key) => {
                groupedPicks.push(groups[+key].pick);
                const legs = groups[+key].pick.getLegs();
                legs.forEach((leg: BetslipPick) => {
                    groupPicks.push(leg);
                });
            });

            picks.forEach((pick) => {
                if (!groupPicks.find((x) => x.id.id === pick.pick.id.id)) {
                    pickList.push({ index: pick.index, pick: pick.pick });
                } else {
                    const group = groupedPicks.find((x) => x.groupInfo.groupId === pick.pick.groupInfo!.groupId);
                    if (!pickList.find((x) => x.pick.id.id === group!.id.id)) {
                        pickList.push({ index: pick.index, pick: group as BetslipPick });
                    }
                }
            });
        } else {
            picks.forEach((pick) => {
                pickList.push({ index: pick.index, pick: pick.pick });
            });
        }

        return betslip.betSlipType === BetSlipType.Single
            ? picks.map((p) => {
                  const acceptedPrice = p.pick.acceptedPrice.price!;

                  return {
                      slipId: getSingleSlipId(p.pick.id),
                      index: p.index,
                      type: new SingleSlipType(),
                      isRemoved: false,
                      changes: {},
                      stake: new Decimal(betslip.stake.amount),
                      picks: [
                          {
                              index: p.index,
                              changes: {},
                              isBanker: false,
                              isEachWay: isPickEachWayCapable(p.pick) && isEachWay(p.pick.id, state),
                              price: {
                                  type: p.pick.priceType,
                                  nativeOdds: p.pick.priceType === PriceType.Fixed ? acceptedPrice.nativeOdds : emptyOdds,
                              },
                              pick: p.pick,
                              oddsState: p.pick.oddsState,
                          },
                      ],
                      promoTokens: betslip.promoTokens,
                      edsPromoTokens: betslip.edsPromoTokens,
                  };
              })
            : [
                  // overask only initialized if we have same-type & single-system-type (linear can have trixie + 2Of3)
                  // so no need to create multiple bets here
                  {
                      slipId:
                          betslip.betSlipType === BetSlipType.Combo
                              ? getComboSlipId()
                              : getSystemSlipIdFromString(reconvertSystem(betslip.systemSlipDetails.systemType)),
                      index: 1,
                      isRemoved: false,
                      stake: new Decimal(betslip.stake.amount),
                      type: betslip.betSlipType === BetSlipType.Combo ? new ComboSlipType() : this.getCurrentSystemType(state),
                      betCount: this.getBetCount(state),
                      changes: {},
                      picks: pickList.map((p) => {
                          const bet = betslip.bets.find((b) => b.index === p.index)!;
                          const acceptedPrice = p.pick.acceptedPrice.price!;

                          return {
                              index: p.index,
                              isEachWay: isPickEachWayCapable(p.pick) && isEachWay(p.pick.id, state),
                              isBanker: !!bet?.isBanker,
                              changes: {},
                              pick: p.pick,
                              price: {
                                  type: p.pick.priceType,
                                  nativeOdds: p.pick.priceType === PriceType.Fixed ? acceptedPrice.nativeOdds : emptyOdds,
                              },
                              oddsState: p.pick.oddsState,
                          };
                      }),
                      promoTokens: betslip.promoTokens,
                      edsPromoTokens: betslip.edsPromoTokens,
                  },
              ];
    }

    private getCurrentSystemType(state: IBetslipState): SlipType {
        if (state.base.displayMode === BetslipDisplayMode.Tabbed) {
            return state.types.systemBet.systemInfo!;
        }

        const typeStakes = state.types.systemBet.linearTypeStakes;

        // this flow should only be invoked if we have exactly 1 linear system type
        return values(typeStakes).find((t) => t.actualStake)!.slipType;
    }

    // undefined for combo, systemCount for system
    private getBetCount(state: IBetslipState): number | undefined {
        if (state.base.displayMode === BetslipDisplayMode.Tabbed) {
            return undefined;
        }

        const typeStakes = state.types.systemBet.linearTypeStakes;

        return values(typeStakes).find((t) => !!t.actualStake)?.betCount;
    }
}
